import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";

import NoDataCreated from "../../Components/NoDataCreated";
import ErrorPage from "../../Components/ErrorPage";
import { goToTop } from "../../Components/goToTop";
import ProgressModal from "../Project/ProjectDetail/ProgressModal";

import {
  QuickAccessList,
  QuickAccessProjects,
} from "../../Controllers/Types/Home/QuickAccessList";

import { Project } from "../../Controllers/Types/Project/ProjectInterface";
import { Service } from "../../Controllers/Types/Project/Service/ServiceInterface";
import { ShareContact } from "../../Controllers/Types/Project/ShareContactInterface";

const QuickAccessFolders: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useRef<Toast>(null);
  const loadingPage = "";
  const error = "";

  /* Progress */
  const [showProgressModal, setShowProgressModal] = useState<boolean>(false);

  const handleCloseProgressModal = () => {
    setShowProgressModal(false);
  };

  const [quickAccessData, setQuickAccessData] =
    useState<QuickAccessList | null>(null);

  const [selectedFolderData, setSelectedFolderData] =
    useState<QuickAccessProjects | null>(null);

  useEffect(() => {
    if (location.state && location.state.quickAccessData) {
      setQuickAccessData(location.state.quickAccessData);
    }
  }, [location.state]);

  const handleAddProgress = (folder: QuickAccessProjects) => {
    setShowProgressModal(true);
    setSelectedFolderData(folder);
  };

  const tableActionBody = (rowData: QuickAccessProjects) => {
    return (
      <div className="tabel_btn_area mt-1 mb-1 gap-3">
        <Button
          icon={`bi bi-clock-history`}
          label={"Progress"}
          className="tabel_btn view_btn fixed_width"
          onClick={() => handleAddProgress(rowData)}
        />

        <Button
          icon={`bi bi-eye`}
          label={"View Folder"}
          className="tabel_btn view_btn fixed_width"
          onClick={() => handleViewFolder(rowData)}
        />
      </div>
    );
  };

  const handleViewFolder = (folder: QuickAccessProjects) => {
    setSelectedFolderData(folder);
    const service = folder.folder_data as Service;
    const selectedProject = folder.project_data as Project;
    const serviceAssignedContact: ShareContact[] = service.assign;
    const itemData = {
      project_id: selectedProject?.project_id,
      job_number: selectedProject?.job_number,
      address: selectedProject?.address,
      folder_id: folder?.folder_id,
      folder: service.folder,
      service_name: folder?.service_name,
      folder_type: folder?.folder_type,
      serviceAssignedContact: serviceAssignedContact,
      projectAllContact: selectedProject?.contact!,
      sharedBy: service.shared_by,
    };
    if (service.type === "Custom") {
      if (service.folder_type === "Doc Drawing") {
        navigate(`/project/detail/custom-service/document-drawing`, {
          state: { data: itemData, folderId: service.folder_id },
        });
      } else if (service.folder_type === "Drawing") {
        navigate(`/project/detail/custom-service/drawing`, {
          state: { data: itemData, folderId: service.folder_id },
        });
      } else {
        navigate(`/project/detail/custom-service/document`, {
          state: { data: itemData, folderId: service.folder_id },
        });
      }
    } else if (service.type === "Default") {
      if (service.folder === "Pre Comencencemet") {
        navigate(
          `default-service/pre-commencement/${service.feature_key_name}`,
          { state: { data: itemData, folderId: service.folder_id } }
        );
      } else if (service.folder === "Onsite") {
        navigate(`default-service/onsite/${service.feature_key_name}`, {
          state: { data: itemData, folderId: service.folder_id },
        });
      } else {
        navigate(`default-service/general/${service.feature_key_name}`, {
          state: { data: itemData, folderId: service.folder_id },
        });
      }
    }
  };

  /* ***************************** Common function ***************************** */
  const goBack = () => {
    navigate(-1);
    goToTop();
  };
  /* *************************************************************************** */

  return (
    <React.Fragment>
      <Toast ref={toast} />

      <div className="main_content layout_padding show_side_bar">
        {loadingPage ? (
          <div className="loading_container">
            <ProgressSpinner
              className="progresscircle custom"
              style={{ width: "50px", height: "50px", stroke: "#8953D7" }}
              strokeWidth="8"
              fill="var(--surface-ground)"
              animationDuration="1s"
            />
          </div>
        ) : error ? (
          <ErrorPage errorMessage={error} />
        ) : (
          <div className="page_content mt-0">
            <div className="page_breadcrumb_area">
              <ul className="page_breadcrumb">
                <li
                  className="page_breadcrumb_item has_link has_child"
                  onClick={() => navigate(-1)}
                >
                  Quick access
                </li>
                <li className="page_breadcrumb_item active">
                  {quickAccessData && quickAccessData?.display_name
                    ? quickAccessData?.display_name
                    : ""}
                </li>
              </ul>

              <button className="back_btn p-ripple" onClick={goBack}>
                <i className="bi bi-arrow-left me-2"></i>
                Back
                <Ripple />
              </button>
            </div>

            <div className="page_main_content_section p-0">
              {quickAccessData && quickAccessData !== null ? (
                <DataTable
                  value={quickAccessData?.projects}
                  paginator
                  size="small"
                  rows={10}
                  emptyMessage="No data found!"
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  tableStyle={{ minWidth: "50rem" }}
                  paginatorDropdownAppendTo={"self"}
                  paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                  currentPageReportTemplate="{first} to {last} of {totalRecords}"
                  className="page_table p-0 p-sm-2"
                  rowHover
                >
                  <Column
                    header="Project address"
                    headerClassName="text_purple"
                    field="job_number"
                    body={(rowData: QuickAccessProjects) => (
                      <span className="text-capitalize text_bold">
                        {rowData?.address ? rowData?.address : "---"}
                      </span>
                    )}
                    style={{ width: "35%" }}
                  ></Column>

                  <Column
                    header="Service name"
                    headerClassName="text_purple"
                    field="project_type"
                    body={(rowData: QuickAccessProjects) => (
                      <span className="text-capitalize">
                        {rowData?.service_name ? rowData?.service_name : "---"}
                      </span>
                    )}
                    style={{ width: "20%" }}
                  ></Column>

                  <Column
                    header="Type"
                    headerClassName="text_purple"
                    field="project_type"
                    body={(rowData: QuickAccessProjects) => (
                      <span className="text-capitalize">
                        {rowData?.folder_type ? rowData?.folder_type : "---"}
                      </span>
                    )}
                    style={{ width: "20%" }}
                  ></Column>

                  <Column
                    body={tableActionBody}
                    alignHeader="center"
                    className="table_btn_area"
                    style={{ width: "25%" }}
                  ></Column>
                </DataTable>
              ) : (
                <NoDataCreated message={`No data found!`} />
              )}
            </div>
          </div>
        )}
      </div>


      {/* ***************************************** Progress modal *************************************** */}
      <ProgressModal
        isOpen={showProgressModal}
        onClose={handleCloseProgressModal}
        toast={toast}
        selectedServiceName={selectedFolderData?.service_name}
        selectedServiceId={selectedFolderData?.folder_id!}
        projectDetails={selectedFolderData?.project_data}
      />
      {/* ************************************************************************************************ */}
    </React.Fragment>
  );
};

export default QuickAccessFolders;
